import { ChangeEvent, ComponentPropsWithoutRef, ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

/** The DOM hands the value back as a string, so booleans have to survive the round trip. */
const parseValue = (value: string): boolean | string => {
    if (value === 'true') {
        return true
    }

    if (value === 'false') {
        return false
    }

    return value
}

/** What the office forms read in their change handlers: the field name and its new value. */
export interface CheckButtonChangeEvent {
    target: { name?: string; value: boolean | string }
}

interface Props extends Omit<
    ComponentPropsWithoutRef<'input'>,
    'className' | 'checked' | 'onChange' | 'type' | 'value'
> {
    className?: string
    /** Truthy shows the on state; strings are allowed because some filters store '1'. */
    checked: boolean | string | undefined
    value?: boolean | string | number
    msg?: ReactNode
    inlineBlock?: boolean
    /** For fields phrased the other way round ("Disabled"), where off is the on state. */
    reverse?: boolean
    /** The on state is the bad one here, so it turns red instead of green. */
    negative?: boolean
    error?: boolean | string
    defaultErrorMsg?: string
    onChange: (event: CheckButtonChangeEvent) => void
    onBeforeChange?: () => void
}

const CheckButton = ({
    className,
    checked,
    value,
    msg,
    inlineBlock = false,
    reverse = false,
    negative = false,
    error,
    defaultErrorMsg,
    onChange,
    onBeforeChange,
    ...inputProps
}: Props) => {
    const isOn = reverse ? !checked : Boolean(checked)
    const isDisabled = inputProps.disabled === true

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        onBeforeChange?.()
        onChange({
            ...event,
            target: { name: inputProps.name, value: parseValue(event.target.value) },
        })
    }

    return (
        <>
            <label className={twMerge('font-semibold', error && 'border border-red-600')}>
                <div
                    className={twMerge(
                        'rounded-sm p-[5px] text-[13px] transition-colors duration-200',
                        inlineBlock ? 'inline-block' : 'block',
                        isOn
                            ? twMerge(
                                  'text-white shadow-[0_0_15px_-5px] [text-shadow:0_0_5px_rgba(255,255,255,0.47)] [&_i]:text-white',
                                  negative
                                      ? 'bg-churned-red shadow-churned-red'
                                      : 'bg-success shadow-success',
                              )
                            : 'bg-grey-200 text-grey-800 [text-shadow:1px_1px_rgba(255,255,255,0.65)] [&_i]:text-grey-800',
                        isDisabled
                            ? 'cursor-not-allowed'
                            : 'cursor-pointer hover:shadow-[0_1px_4px_rgba(32,33,36,0.18),0_1px_26px_rgba(32,33,36,0.08)]',
                        className,
                    )}>
                    <input
                        {...inputProps}
                        className="hidden"
                        onChange={handleChange}
                        type="checkbox"
                        value={value === undefined ? undefined : String(value)}
                    />
                    {msg}
                </div>
            </label>
            {error && (
                <div className="text-[13px] leading-none text-red-600">
                    {(typeof error === 'string' && error) || defaultErrorMsg || 'check this field'}
                </div>
            )}
        </>
    )
}

export default CheckButton
